import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { ServicesBuilder } from '../utils/setup';
import { Category, CategoryService, ICategory, ICategoryService, ISubcategory } from '@posx/core';
import { Subscription } from 'dexie';
import { logLiveQueryError, isLiveQueryCursorError } from '@/utils/logger';

export const useCategoryStore = defineStore('category', {
  state: () => ({
    page: 1 as number,
    totalPage: 0 as number,
    categories: [] as ICategory[],
    filtersValues: {},
    categoryMap: {} as { [key: string]: ICategory },
    categoryService: null as ICategoryService | null,
    _subscription: null as Subscription | null,
  }),
  getters: {
    sortedList: (state) => [...state.categories].sort((a, b) => a.sequence - b.sequence)
  },
  actions: {
    async getService() {
      if (!this.categoryService)
        // service singleton is method-only, skip deep proxy
        this.categoryService = markRaw(await ServicesBuilder.getInstance().getService<ICategoryService>(CategoryService));
      return this.categoryService;
    },
    async startLiveQuery() {
      if (this._subscription) return;
      const service = await this.getService();
      // use table.filter() directly to enable Dexie reactive updates when subcategories change
      const sub: Subscription = service.liveQuery(() =>
        service.table.filter(item => item.deleted_at == null).toArray()
      ).subscribe({
        next: (result: ICategory[]) => {
          // markRaw each category: entities are structurally stable between rebuilds, skip deep proxy overhead
          this.categories = (result || []).map(markRaw);
          const map: Record<string, ICategory> = {}; // O(n) mutable build avoids spread GC pressure
          for (const item of this.categories) map[item.uid] = item;
          this.categoryMap = map;
          this.totalPage = result?.length ?? 0;
        },
        // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
        error: (err) => { logLiveQueryError(err, { store: 'category' }); if (isLiveQueryCursorError(err) && this._subscription === sub) { sub.unsubscribe(); this._subscription = null; } }
      });
      this._subscription = markRaw(sub);
    },
    stopLiveQuery() {
      if (this._subscription) {
        this._subscription.unsubscribe();
        this._subscription = null;
      }
    },
    setFilterValue(values: Partial<Category>) {
      this.filtersValues = values;
    },
    async add(payload: Category) {
      const service = await ServicesBuilder.getInstance().getService<CategoryService>(CategoryService);
      return service?.addOne(payload);
    },
    async update(payload: Category) {
      const service = await ServicesBuilder.getInstance().getService<CategoryService>(CategoryService);
      return service?.updateOne(payload.id_in_server, payload);
    },

    async delete(model: Category) {
      const service = await ServicesBuilder.getInstance().getService<CategoryService>(CategoryService);
      return service?.deleteOne(model);
    },

    async addSubCategory(category: Category, subCategory: ISubcategory) {
      const service = await ServicesBuilder.getInstance().getService<ICategoryService>(CategoryService);
      return service?.addSubcategory(category, subCategory);
    },
    async updateSubCategory(category: Category, subCategory: ISubcategory) {
      const service = await ServicesBuilder.getInstance().getService<ICategoryService>(CategoryService);
      return service?.updateSubcategory(category, subCategory);
    },
    async removeSubCategory(category: Category, subcategoryUid: string) {
      const service = await ServicesBuilder.getInstance().getService<ICategoryService>(CategoryService);
      return service?.removeSubcategory(category, subcategoryUid);
    },
    async getAll() {
      const service = await ServicesBuilder.getInstance().getService<CategoryService>(CategoryService);
      const result = await service?.getMany(this.filtersValues, { page: 1, pageSize: 1000 }); // Set large pageSize to get all
      this.categories = (result?.list || []).map(markRaw);
      const map: Record<string, ICategory> = {}; // O(n) mutable build avoids spread GC pressure
      for (const item of this.categories) map[item.uid] = item;
      this.categoryMap = map;
      return this.sortedList;
    }
  },
});
